import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

// ========== Project Types ==========
export function useProjectTypes(params?: { cursor?: string; limit?: number }) {
  return useQuery({
    queryKey: ['project-types', params],
    queryFn: async () => {
      try {
        return await apiClient.get('/api/v1/projects/types', params as Record<string, unknown> | undefined);
      } catch {
        // pm-api: /projects/types kollidiert aktuell mit /projects/:id-Route — leere Liste
        return { success: false, data: [] };
      }
    },
    retry: false,
  });
}

export function useCreateProjectType() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (data: Record<string, unknown>) => apiClient.post('/api/v1/projects/types', data), onSuccess: () => qc.invalidateQueries({ queryKey: ['project-types'] }) });
}

export function useUpdateProjectType() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => apiClient.patch(`/api/v1/projects/types/${id}`, data), onSuccess: () => qc.invalidateQueries({ queryKey: ['project-types'] }) });
}

export function useDeleteProjectType() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/types/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['project-types'] }) });
}

// ========== Cost Elements ==========
export function useCostElements(params?: { cursor?: string; limit?: number }) {
  return useQuery({
    queryKey: ['cost-elements', params],
    queryFn: async () => {
      try {
        return await apiClient.get('/api/v1/projects/cost-elements', params);
      } catch {
        // pm-api: /projects/cost-elements kollidiert aktuell mit /projects/:id-Route — leere Liste
        return { success: false, data: [] };
      }
    },
    retry: false,
  });
}

export function useCreateCostElement() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (data: Record<string, unknown>) => apiClient.post('/api/v1/projects/cost-elements', data), onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-elements'] }) });
}

export function useUpdateCostElement() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => apiClient.patch(`/api/v1/projects/cost-elements/${id}`, data), onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-elements'] }) });
}

export function useDeleteCostElement() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/cost-elements/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-elements'] }) });
}

// ========== Projects ==========
export function useProjects(params?: Record<string, unknown>) {
  return useQuery({ queryKey: ['projects', params], queryFn: () => apiClient.get('/api/v1/projects', params) });
}

export function useProject(id: string | undefined) {
  return useQuery({ queryKey: ['projects', id], queryFn: () => apiClient.get(`/api/v1/projects/${id}`), enabled: !!id });
}

/**
 * Schlanker Bulk-Overview der Schritt-Reifegrade je Projekt — für die
 * Timeline-Einfärbung (Ampel-Summe). Default: nur aktive Projekte
 * (state NOT IN COMPLETED/CANCELED). Liefert pro Projekt steps: number[18]
 * (Schritt 1..18, roh 0-5). Reiner Read, kein lazy-Insert. Degradiert auf
 * leer, solange der Endpoint noch nicht deployed ist.
 */
export function useReifegradOverview(params?: { state?: string }) {
  return useQuery({
    queryKey: ['projects', 'reifegrad-overview', params ?? null],
    queryFn: async () => {
      try {
        return await apiClient.get('/api/v1/projects/reifegrad-overview', params as Record<string, unknown> | undefined);
      } catch {
        return { success: false, data: [] };
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

interface PagedProjects {
  data?: Record<string, unknown>[];
  pagination?: { cursor: string | null; hasMore: boolean };
}

/**
 * Lädt ALLE Projekte tenant-weit (alle Seiten) via Cursor-Pagination.
 * Für Ansichten wie die Timeline, die nicht seitenweise blättern, sondern
 * den Gesamtbestand brauchen. Sicherung gegen Endlosschleifen: max. 20 Seiten
 * à 100 = 2000 Projekte; darüber wird abgeschnitten (capped-Flag).
 */
export function useAllProjects(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['projects', 'all', params],
    queryFn: async () => {
      const acc: Record<string, unknown>[] = [];
      let cursor: string | undefined;
      let capped = false;
      const MAX_PAGES = 20;
      for (let page = 0; page < MAX_PAGES; page++) {
        const resp = (await apiClient.get('/api/v1/projects', {
          ...params,
          cursor,
          limit: 100,
        })) as PagedProjects;
        if (resp.data?.length) acc.push(...resp.data);
        const next = resp.pagination;
        if (next?.hasMore && next.cursor) {
          cursor = next.cursor;
          if (page === MAX_PAGES - 1) capped = true;
        } else {
          break;
        }
      }
      return { data: acc, capped };
    },
    staleTime: 60_000,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (data: Record<string, unknown>) => apiClient.post('/api/v1/projects', data), onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }) });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => apiClient.patch(`/api/v1/projects/${id}`, data),
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: ['projects'] }); qc.invalidateQueries({ queryKey: ['projects', v.id] }); },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }) });
}

// ========== Participants ==========
export function useProjectParticipants(projectId: string | undefined) {
  return useQuery({ queryKey: ['project-participants', projectId], queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/participants`), enabled: !!projectId });
}

export function useAddParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: Record<string, unknown> }) => apiClient.post(`/api/v1/projects/${projectId}/participants`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-participants'] }),
  });
}

export function useRemoveParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, participantId }: { projectId: string; participantId: string }) => apiClient.delete(`/api/v1/projects/${projectId}/participants/${participantId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-participants'] }),
  });
}

// ========== Tasks ==========
export function useProjectTasks(projectId: string | undefined, view?: string) {
  return useQuery({
    queryKey: ['project-tasks', projectId, view],
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/tasks`, { view }),
    enabled: !!projectId,
  });
}

/** Liefert die dem angemeldeten User zugewiesenen Tasks (tenant-weit, alle Projekte).
 *  Default exkludiert erledigte Tasks via status_not=done. */
export function useMyTasks(params?: { status_not?: string; limit?: number; sort?: string }) {
  const query = { status_not: 'done', ...params };
  return useQuery({
    queryKey: ['my-tasks', query],
    queryFn: () => apiClient.get('/api/v1/tasks/me', query as Record<string, unknown>),
    retry: false, // 500 vom PM bei bug-fix-wave nicht endlos retryen
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: Record<string, unknown> }) => apiClient.post(`/api/v1/projects/${projectId}/tasks`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-tasks'] }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId, data }: { projectId: string; taskId: string; data: Record<string, unknown> }) => apiClient.patch(`/api/v1/projects/${projectId}/tasks/${taskId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-tasks'] }),
  });
}

export function useUpdateTaskKanbanStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId, kanban_status }: { projectId: string; taskId: string; kanban_status: 'Offen' | 'In Arbeit' | 'Review' | 'Done' }) =>
      apiClient.patch(`/api/v1/projects/${projectId}/tasks/${taskId}`, { kanban_status }),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'kanban-c'] });
      qc.invalidateQueries({ queryKey: ['project-tasks'] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId }: { projectId: string; taskId: string }) => apiClient.delete(`/api/v1/projects/${projectId}/tasks/${taskId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-tasks'] }),
  });
}

export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId, data }: { projectId: string; taskId: string; data: Record<string, unknown> }) => apiClient.post(`/api/v1/projects/${projectId}/tasks/${taskId}/move`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-tasks'] }),
  });
}

// ========== Milestones ==========
export function useProjectMilestones(projectId: string | undefined) {
  return useQuery({ queryKey: ['project-milestones', projectId], queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/milestones`), enabled: !!projectId });
}

export function useCreateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: Record<string, unknown> }) => apiClient.post(`/api/v1/projects/${projectId}/milestones`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-milestones'] }),
  });
}

export function useUpdateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, milestoneId, data }: { projectId: string; milestoneId: string; data: Record<string, unknown> }) => apiClient.patch(`/api/v1/projects/${projectId}/milestones/${milestoneId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-milestones'] }),
  });
}

export function useDeleteMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, milestoneId }: { projectId: string; milestoneId: string }) => apiClient.delete(`/api/v1/projects/${projectId}/milestones/${milestoneId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-milestones'] }),
  });
}

// ========== Work Logs ==========
export function useProjectWorkLogs(projectId: string | undefined, params?: Record<string, unknown>) {
  return useQuery({ queryKey: ['project-worklogs', projectId, params], queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/work-logs`, params), enabled: !!projectId });
}

/**
 * Zeitbuchung auf ein Projekt.
 *
 * 🔴 AUFLAGE, und sie ist gegenseitig — NICHT ohne Rücksprache mit der pm-api brechen:
 *
 *   **In `data` darf kein `ticket_id` stehen.**
 *
 * Warum das mehr ist als eine Stilfrage: Am work-logs-Endpunkt der pm-api hängt eine
 * Buchungssperre für unvollständige Ticketrümpfe. Sie greift NUR, wenn eine `ticket_id`
 * mitgeschickt wird — `pruefeTicketRumpf` steigt vorher aus und ruft die ticket-api
 * gar nicht erst. Genau dieser Ausstieg hat am 18.09.2026 die Projektzeiterfassung aus
 * einer PROD-Störung herausgehalten, die alle Ticketbuchungen blockierte.
 *
 * Wer hier einen Ticketbezug einbaut, hängt die Projektzeiterfassung an die
 * Erreichbarkeit eines Dienstes, mit dem sie nichts zu tun hat. Der Fehler fällt nicht
 * auf, solange die ticket-api läuft — er zeigt sich erst bei deren nächster Störung.
 *
 * Die Gegenseite hat zugesagt, den Ausstieg nicht zu entfernen (Kommentar über der
 * Zeile in `WorkLogService.pruefeTicketRumpf`). **Keine der beiden Zusagen hält
 * allein.** Abgestimmt mit pm-api und SMS PRO am 15. und 21.09.2026.
 *
 * Gemessen am 18.09.2026: `ticket_id` kommt im Schreibpfad von PHQ nicht vor; der
 * einzige Treffer im Projekt ist ein Lesefeld in `useUserWorkLogs`.
 */
export function useCreateWorkLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: Record<string, unknown> }) => apiClient.post(`/api/v1/projects/${projectId}/work-logs`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-worklogs'] }); qc.invalidateQueries({ queryKey: ['project-time-summary'] }); },
  });
}

export function useUpdateWorkLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, workLogId, data }: { projectId: string; workLogId: string; data: Record<string, unknown> }) => apiClient.patch(`/api/v1/projects/${projectId}/work-logs/${workLogId}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-worklogs'] }); qc.invalidateQueries({ queryKey: ['project-time-summary'] }); },
  });
}

export function useDeleteWorkLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, workLogId }: { projectId: string; workLogId: string }) => apiClient.delete(`/api/v1/projects/${projectId}/work-logs/${workLogId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-worklogs'] }); qc.invalidateQueries({ queryKey: ['project-time-summary'] }); },
  });
}

// ========== Task Participants ==========
export function useTaskParticipants(projectId: string | undefined, taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-participants', projectId, taskId],
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/tasks/${taskId}/participants`),
    enabled: !!projectId && !!taskId,
  });
}

export function useAddTaskParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId, data }: { projectId: string; taskId: string; data: Record<string, unknown> }) =>
      apiClient.post(`/api/v1/projects/${projectId}/tasks/${taskId}/participants`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task-participants'] }),
  });
}

export function useRemoveTaskParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId, participantId }: { projectId: string; taskId: string; participantId: string }) =>
      apiClient.delete(`/api/v1/projects/${projectId}/tasks/${taskId}/participants/${participantId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task-participants'] }),
  });
}

// ========== Task Skills ==========
export function useTaskSkills(projectId: string | undefined, taskId: string | undefined) {
  return useQuery({
    queryKey: ['task-skills', projectId, taskId],
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/tasks/${taskId}/skills`),
    enabled: !!projectId && !!taskId,
  });
}

export function useAllSkillNames() {
  return useQuery({
    queryKey: ['skill-names'],
    queryFn: () => apiClient.get('/api/v1/skill-names'),
  });
}

export function useSetTaskSkills() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId, skills }: { projectId: string; taskId: string; skills: string[] }) =>
      apiClient.put(`/api/v1/projects/${projectId}/tasks/${taskId}/skills`, { skills }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-skills'] });
      qc.invalidateQueries({ queryKey: ['skill-names'] });
    },
  });
}

// ========== Billing ==========
export function useProjectTimeSummary(projectId: string | undefined) {
  return useQuery({ queryKey: ['project-time-summary', projectId], queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/time-summary`), enabled: !!projectId });
}

export function useGanttData(projectId: string | undefined) {
  return useQuery({ queryKey: ['project-gantt', projectId], queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/gantt-data`), enabled: !!projectId });
}

export function useProjectBillingSummary(projectId: string | undefined) {
  return useQuery({ queryKey: ['project-billing', projectId], queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/billing/summary`), enabled: !!projectId });
}

export function useBookWorkLogs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: Record<string, unknown> }) => apiClient.post(`/api/v1/projects/${projectId}/work-logs/book`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-worklogs'] }); qc.invalidateQueries({ queryKey: ['project-billing'] }); },
  });
}

export function useUnbookWorkLogs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: Record<string, unknown> }) => apiClient.post(`/api/v1/projects/${projectId}/work-logs/unbook`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-worklogs'] }); qc.invalidateQueries({ queryKey: ['project-billing'] }); },
  });
}
